/* eslint-disable max-len */
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { decode, encodeURI } from 'js-base64';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import * as queryString from 'query-string';
import Paginator from 'components/Pagination/Paginator';
import ResultsPerPage from 'components/Pagination/ResultsPerPage';
import { IStore } from 'store/types';
import { IApiQueryParam } from 'types';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;

const ListContext = createContext<any>(null);
export interface FetchDataQueryType {
  sort?: string;
  offset: number;
  limit: number;
  q?: IApiQueryParam[];
  meta?: unknown;
}

interface IListContextProviderProps {
  children: React.ReactNode;
  defaultOffset?: number;
  defaultLimit?: number;
  defaultSort?: string;
  defaultFilters?: IApiQueryParam[];
  persistURL?: boolean;
  fetchData: (query: FetchDataQueryType) => void;
  totalSelector: (state: IStore) => number | undefined | null;
  dataKey: string;
  dependencies?: unknown;
}

/* This provider wraps children components and provides them encapsulated data and methods to negotiate list-structured data from the API. It allows components to sort, paginate, search, and query data as well as optionally persist it through the URL.
 */

export const ListContextProvider = ({
  children,
  defaultOffset = DEFAULT_OFFSET,
  defaultLimit = DEFAULT_LIMIT,
  defaultSort,
  defaultFilters, // otherwise known as q
  persistURL = false,
  fetchData,
  totalSelector,
  dataKey,
  dependencies,
}: IListContextProviderProps) => {
  const location = useLocation();
  const history = useHistory();

  const resultsLength = useSelector(totalSelector);
  /*
   * We are using the dependencies ref to trigger the useEffect only when dependencies change
   * https://www.benmvp.com/blog/object-array-dependencies-react-useEffect-hook/
   */
  const prevRefValue = useRef({});
  const dependenciesRef = useRef(dependencies);

  if (!isEqual(dependenciesRef.current, dependencies)) {
    prevRefValue.current = {};
    dependenciesRef.current = dependencies;
  }

  const pushEncodedURIForQuery = (query: { limit: number; offset: number; sort?: string; q?: IApiQueryParam[] }) => {
    const stringifiedQuery = JSON.stringify({ [dataKey]: query });
    const encodedURIQuery = encodeURI(stringifiedQuery);
    history.push({
      pathname: location.pathname,
      search: `data=${encodedURIQuery}`,
    });
  };

  const onPageChange = (page: number) => {
    const newValue = {
      ...value,
      offset: page * value.limit,
    };

    if (persistURL) {
      pushEncodedURIForQuery(newValue);
    } else {
      setValue(newValue);
    }
  };

  const onLimitChange = (newLimit: number) => {
    const newValue = {
      ...value,
      limit: newLimit,
      offset: DEFAULT_OFFSET,
    };

    if (persistURL) {
      pushEncodedURIForQuery(newValue);
    } else {
      setValue(newValue);
    }
  };

  const onSortChange = (newSort: string) => {
    const newValue = {
      ...value,
      sort: value?.sort === newSort ? `-${newSort}` : newSort,
      offset: DEFAULT_OFFSET,
    };

    if (persistURL) {
      pushEncodedURIForQuery(newValue);
    } else {
      setValue(newValue);
    }
  };

  const onFiltersChange = (filters: IApiQueryParam[], force = false) => {
    const newValue = {
      ...value,
      q: filters,
      offset: DEFAULT_OFFSET,
    };
    if (persistURL) {
      pushEncodedURIForQuery(newValue);
    } else {
      if (force === true) prevRefValue.current = {}; // if force, clear the prevRefValue to force an update in the useEffect
      setValue(newValue);
    }
  };

  const isSortDesc = (): boolean => value?.sort?.[0] === '-';

  const isSortedBy = (headerValue: string): boolean => value?.sort === headerValue || value?.sort === `-${headerValue}`;

  const { data: qsData } = queryString.parse(location.search);

  const urlData = (() => {
    if (persistURL && qsData) {
      try {
        if (qsData) {
          const decodedData = decode(qsData as string);
          return JSON.parse(decodedData)?.[dataKey];
        }
      } catch (e) {
        return undefined;
      }
    }
    return null;
  })();

  // initialize the previous values so we can compare for any changes.
  // This is used in the useEffect that calls to fetch data.

  const [value, setValue] = useState<{
    limit: number;
    offset: number;
    sort?: string;
    q?: any[];
  }>(
    urlData || {
      limit: defaultLimit,
      offset: defaultOffset,
      ...(defaultSort ? { sort: defaultSort } : {}),
      ...(defaultFilters ? { q: defaultFilters } : {}),
    }
  );

  const callbackFetchData = useCallback(fetchData, [value]);

  // if persistURL is true, we use the URL decoded query parameters as the source of truth and change state whenever the URL data changes
  useEffect(() => {
    if (persistURL && qsData) {
      const urlData = (() => {
        try {
          if (qsData) {
            const decodedData = decode(qsData as string);
            return JSON.parse(decodedData)?.[dataKey];
          }
          return value;
        } catch (e) {
          return value;
        }
      })();

      setValue(urlData);
    }
  }, [persistURL, dataKey, qsData]);

  const fetchCacheData = () => {
    if (!isEqual(prevRefValue.current, value)) {
      prevRefValue.current = value;
      callbackFetchData({ ...value, meta: dependencies });
    }
  };

  // changes in query parameters (values) or the dependancyRef should cause a new query fetch
  useEffect(() => {
    fetchCacheData();
  }, [value]);

  // In case any dependency changes the page should go back to 1
  useEffect(() => {
    if (dependenciesRef?.current) {
      setValue({
        ...value,
        ...{ offset: defaultOffset },
      });
    } else {
      fetchCacheData();
    }
  }, [dependenciesRef.current]);

  const providerValue = {
    query: value,
    methods: {
      isSortDesc,
      isSortedBy,
      onPageChange,
      onLimitChange,
      onSortChange,
      onFiltersChange,
      fetchData: callbackFetchData,
    },
    components: {
      pagination: !isNil(resultsLength) && resultsLength > 0 ? <Paginator onPageChange={onPageChange} numRecords={resultsLength} limit={value.limit} offset={value.offset} /> : null,
      resultsPerPage: !isNil(resultsLength) && resultsLength > 0 ? <ResultsPerPage pageSize={value.limit} onChange={(value: number) => onLimitChange(value)} /> : null,
    },
  };

  return <ListContext.Provider value={providerValue}>{children}</ListContext.Provider>;
};

export default ListContext;
